import { SupabaseClient } from '@supabase/supabase-js';
import { DocumentRepository } from '../repositories/document.repository';
import { ProjectUserRepository } from '../repositories/project-user.repository';
import { DocumentDTO } from '../dtos';
import { DocumentMapper } from '../mappers/document.mapper';

export interface ImportSlackRequest {
  slackMessageId: string;
  slackChannelId: string;
  projectId: string;
  slackApiToken?: string; // Optional for backward compatibility
}

export interface ImportSlackResponse {
  success: boolean;
  document?: DocumentDTO;
  message?: string;
  error?: string;
}

export interface SlackMessage {
  id: string;
  text: string;
  user: string;
  channel: string;
  timestamp: string;
  threadTs?: string;
  permalink: string;
  attachments?: SlackAttachment[];
  files?: SlackFile[];
}

export interface SlackAttachment {
  id: string;
  title?: string;
  text?: string;
  color?: string;
  pretext?: string;
  author_name?: string;
  author_link?: string;
  author_icon?: string;
  title_link?: string;
  fields?: SlackField[];
  image_url?: string;
  thumb_url?: string;
  footer?: string;
  footer_icon?: string;
  ts?: string;
}

export interface SlackFile {
  id: string;
  name: string;
  title: string;
  mimetype: string;
  filetype: string;
  pretty_type: string;
  user: string;
  size: number;
  url_private: string;
  url_private_download: string;
  thumb_64?: string;
  thumb_80?: string;
  thumb_360?: string;
  thumb_360_w?: number;
  thumb_360_h?: number;
  permalink: string;
  permalink_public: string;
  is_external: boolean;
  external_type: string;
  is_public: boolean;
  public_url_shared: boolean;
  display_as_bot: boolean;
  username: string;
  timestamp: string;
}

export interface SlackField {
  title: string;
  value: string;
  short: boolean;
}

export interface SlackChannel {
  id: string;
  name: string;
  is_channel: boolean;
  is_group: boolean;
  is_im: boolean;
  is_mpim: boolean;
  is_private: boolean;
  created: number;
  creator: string;
  is_archived: boolean;
  is_general: boolean;
  name_normalized: string;
  is_shared: boolean;
  is_org_shared: boolean;
  is_member: boolean;
  is_pending_ext_shared: boolean;
  pending_shared: any[];
  context_team_id: string;
  updated: number;
  parent_conversation?: string;
  locale: string;
}

export interface SearchSlackMessagesRequest {
  slackApiToken?: string; // Optional - will use OAuth token if not provided
  projectId: string;
  query?: string;
  channelId?: string;
  sort?: 'timestamp' | 'score';
  sort_dir?: 'asc' | 'desc';
  limit?: number;
  page?: number;
}

export interface SearchSlackMessagesResponse {
  success: boolean;
  messages?: SlackMessage[];
  hasMore?: boolean;
  nextCursor?: string;
  error?: string;
}

export interface SlackIntegration {
  id: string;
  userId: string;
  projectId: string;
  accessToken: string;
  teamId: string;
  teamName?: string;
  teamIcon?: string;
  botUserId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SlackOAuthResponse {
  success: boolean;
  authUrl?: string;
  integration?: {
    id: string;
    team_id: string;
    team_name?: string;
    team_icon?: string;
    bot_user_id?: string;
    created_at: string;
  };
  message?: string;
  error?: string;
}

export class SlackService {
  private documentRepository: DocumentRepository;
  private projectUserRepository: ProjectUserRepository;

  constructor(private supabase: SupabaseClient) {
    this.documentRepository = new DocumentRepository(supabase);
    this.projectUserRepository = new ProjectUserRepository(supabase);
  }

  /**
   * 슬랙 메시지를 문서로 가져오기
   */
  async importFromSlack(
    request: ImportSlackRequest,
    userId: string
  ): Promise<ImportSlackResponse> {
    try {
      // 1. 사용자가 프로젝트 멤버인지 확인
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        request.projectId,
        userId
      );
      
      if (!projectUser || projectUser.role === 'viewer') {
        return {
          success: false,
          error: '문서를 생성할 권한이 없습니다.'
        };
      }

      // 2. OAuth 토큰 가져오기 (API 키가 없는 경우)
      let slackApiToken = request.slackApiToken;
      if (!slackApiToken) {
        const accessToken = await this.getSlackAccessToken(userId, request.projectId);
        if (!accessToken) {
          return {
            success: false,
            error: '슬랙 연동이 필요합니다. 먼저 슬랙과 연동해주세요.'
          };
        }
        slackApiToken = accessToken;
      }

      // 3. Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        return {
          success: false,
          error: 'Authentication required - no valid session found'
        };
      }

      // 4. Edge Function 호출
      const { data, error } = await this.supabase.functions.invoke('import-slack', {
        body: {
          slackMessageId: request.slackMessageId,
          slackChannelId: request.slackChannelId,
          projectId: request.projectId,
          slackApiToken,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        return {
          success: false,
          error: error.message || '슬랙 메시지 가져오기에 실패했습니다.'
        };
      }

      // 4. 응답 처리
      if (data?.success && data?.document) {
        // Edge Function에서 생성된 문서를 조회
        const document = await this.documentRepository.findById(data.document.id);
        
        if (!document) {
          return {
            success: false,
            error: '문서 생성은 성공했으나 조회에 실패했습니다.'
          };
        }

        return {
          success: true,
          document: DocumentMapper.toDTO(document),
          message: data.message || '슬랙 메시지를 성공적으로 가져왔습니다.'
        };
      } else {
        return {
          success: false,
          error: data?.error || '알 수 없는 오류가 발생했습니다.'
        };
      }
    } catch (error) {
      console.error('Error importing from Slack:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : '슬랙 메시지 가져오기에 실패했습니다.'
      };
    }
  }

  /**
   * 슬랙 메시지 URL에서 메시지 ID와 채널 ID 추출
   */
  extractMessageInfoFromUrl(url: string): { messageId: string; channelId: string } | null {
    try {
      // 슬랙 메시지 URL 형식 처리
      // https://app.slack.com/client/T1234567890/C1234567890/thread/1234567890.123456
      // https://teamname.slack.com/archives/C1234567890/p1234567890123456
      const archiveMatch = url.match(/\/archives\/([A-Z0-9]+)\/p([0-9]+)/);
      if (archiveMatch) {
        const channelId = archiveMatch[1];
        const timestamp = archiveMatch[2];
        // p1234567890123456 형태를 1234567890.123456 형태로 변환
        const messageId = `${timestamp.slice(0, 10)}.${timestamp.slice(10)}`;
        return { messageId, channelId };
      }

      const threadMatch = url.match(/\/client\/[A-Z0-9]+\/([A-Z0-9]+)\/thread\/([0-9]+\.[0-9]+)/);
      if (threadMatch) {
        const channelId = threadMatch[1];
        const messageId = threadMatch[2];
        return { messageId, channelId };
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * 슬랙 API 토큰 유효성 검증 (선택적)
   */
  validateSlackApiToken(apiToken: string): boolean {
    // 슬랙 API 토큰은 'xoxb-' 또는 'xoxp-'로 시작
    return /^xox[bp]-[0-9A-Za-z\-]+$/.test(apiToken);
  }

  /**
   * 슬랙 메시지 검색
   */
  async searchSlackMessages(
    request: SearchSlackMessagesRequest,
    userId: string
  ): Promise<SearchSlackMessagesResponse> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        return {
          success: false,
          error: 'Authentication required - no valid session found'
        };
      }

      // Edge Function을 통해 검색 수행
      const { data, error } = await this.supabase.functions.invoke('search-slack', {
        body: {
          query: request.query || '',
          channelId: request.channelId,
          projectId: request.projectId,
          sort: request.sort || 'timestamp',
          sort_dir: request.sort_dir || 'desc',
          limit: request.limit || 20,
          page: request.page || 1,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        return {
          success: false,
          error: error.message || '슬랙 메시지 검색에 실패했습니다.'
        };
      }

      if (!data || !data.success) {
        return {
          success: false,
          error: data?.error || '슬랙 메시지 검색에 실패했습니다.'
        };
      }

      return {
        success: true,
        messages: data.messages || [],
        hasMore: data.hasMore,
        nextCursor: data.nextCursor,
      };
    } catch (error) {
      console.error('Error searching Slack messages:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : '슬랙 메시지 검색에 실패했습니다.'
      };
    }
  }

  /**
   * 슬랙 OAuth 인증 시작
   */
  async initiateSlackOAuth(
    userId: string,
    projectId: string
  ): Promise<SlackOAuthResponse> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        return {
          success: false,
          error: 'Authentication required - no valid session found'
        };
      }

      const { data, error } = await this.supabase.functions.invoke('slack-oauth', {
        body: {
          action: 'authorize',
          projectId,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        return {
          success: false,
          error: error.message || '슬랙 OAuth 시작에 실패했습니다.'
        };
      }

      return {
        success: true,
        authUrl: data.authUrl,
      };
    } catch (error) {
      console.error('Error initiating Slack OAuth:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : '슬랙 OAuth 시작에 실패했습니다.'
      };
    }
  }

  /**
   * 슬랙 OAuth 콜백 처리
   */
  async handleSlackOAuthCallback(
    code: string,
    state: string
  ): Promise<SlackOAuthResponse> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        return {
          success: false,
          error: 'Authentication required - no valid session found'
        };
      }

      const { data, error } = await this.supabase.functions.invoke('slack-oauth', {
        body: {
          action: 'callback',
          code,
          state,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        return {
          success: false,
          error: error.message || '슬랙 OAuth 콜백 처리에 실패했습니다.'
        };
      }

      return {
        success: true,
        integration: data.integration,
        message: data.message,
      };
    } catch (error) {
      console.error('Error handling Slack OAuth callback:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : '슬랙 OAuth 콜백 처리에 실패했습니다.'
      };
    }
  }

  /**
   * 슬랙 연동 상태 확인
   */
  async checkSlackIntegration(
    userId: string,
    projectId: string
  ): Promise<{ connected: boolean; integration?: SlackIntegration }> {
    try {
      const { data, error } = await this.supabase
        .from('slack_integrations')
        .select('*')
        .eq('user_id', userId)
        .eq('project_id', projectId)
        .maybeSingle();

      if (error || !data) {
        return { connected: false };
      }

      return {
        connected: true,
        integration: {
          id: data.id,
          userId: data.user_id,
          projectId: data.project_id,
          accessToken: data.access_token,
          teamId: data.team_id,
          teamName: data.team_name,
          teamIcon: data.team_icon,
          botUserId: data.bot_user_id,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
        },
      };
    } catch (error) {
      console.error('Error checking Slack integration:', error);
      return { connected: false };
    }
  }

  /**
   * 슬랙 연동 해제
   */
  async disconnectSlack(
    userId: string,
    projectId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await this.supabase
        .from('slack_integrations')
        .delete()
        .eq('user_id', userId)
        .eq('project_id', projectId);

      if (error) {
        return {
          success: false,
          error: error.message || '슬랙 연동 해제에 실패했습니다.'
        };
      }

      return { success: true };
    } catch (error) {
      console.error('Error disconnecting Slack:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : '슬랙 연동 해제에 실패했습니다.'
      };
    }
  }

  /**
   * 슬랙 액세스 토큰 가져오기 (내부 헬퍼 메서드)
   */
  private async getSlackAccessToken(
    userId: string,
    projectId: string
  ): Promise<string | null> {
    try {
      const { data, error } = await this.supabase
        .from('slack_integrations')
        .select('access_token')
        .eq('user_id', userId)
        .eq('project_id', projectId)
        .maybeSingle();

      if (error || !data) {
        return null;
      }

      return data.access_token;
    } catch (error) {
      console.error('Error getting Slack access token:', error);
      return null;
    }
  }
}